import React, { useEffect, useState } from 'react'
import PageHeader from '../../components/PageHeader'
import { useNavigate, useParams } from 'react-router-dom'
import { notifyError } from '../../utils/notifyError'
import TermsSection from './TermsSectionTable'
import Enrollments from './Enrollments'
import LoadingPageV2 from '../../pages/LoadingPageV2'
import PAGE_TITLES from '../../consts/titles'
import { Box, Typography } from '@mui/material'
import CustomHeader from '../../components/CustomHeader'
import ROUTES from '../../consts/paths'
import { type CustomBreadcrumbT } from '../../utils/BreadcrumbDataPreparation'
import { useQuery } from '@tanstack/react-query'
import { TermsManager } from '../../APIManager'
import { formatTermDateRangeByUserTimeZone } from '../../helpers/formatDateByUserTimeZone'
import GenericModal from '../../components/GenericModal'
import GenericButton from '../../components/GenericButton'
import { CreateAndEditTerms } from '../Terms/CreateAndEditTerms'
import { Actions } from '../../components/CustomTable/types'
import { ButtonType } from '../../components/GenericButton/styles'
import TermsCourseSectionTab, { TabKeys } from './TermsCourseSectionTab'
import { useAppSearchParams } from '../../hooks/useAppSearchParams'
import { Calendar, Pencil } from 'lucide-react'
import { grey } from '../../colors'

export const TermSectionsAndEnrollmentsFeature: React.FC = (): JSX.Element => {
  const { id: termId } = useParams()
  const navigate = useNavigate()
  const [showTermUpdateModal, setShowTermUpdateModal] = useState<boolean>(false)
  const { searchParams } = useAppSearchParams()
  const selectedTab = searchParams.get('tab')

  const { data: singleTermData = [], isLoading: singleTermLoading, isError: isTermError, error: termError } = useQuery({
    queryKey: ['getTermById', termId],
    queryFn: async () => (await TermsManager.getTermById({ termId: termId ?? '' })).data,
    enabled: !!termId
  })

  useEffect(() => {
    if (isTermError) {
      notifyError(termError, { fallbackMessage: 'Failed to load term. Please try again.' })
      navigate(ROUTES.TERMS)
    }
  }, [isTermError])

  const breadcrumbPrefixList: CustomBreadcrumbT[] = [{
    title: PAGE_TITLES.TERMS,
    path: ROUTES.TERMS
  }, {
    title: singleTermData?.data?.termName ?? '',
    path: ROUTES.TERMS_DETAILS
  }]

  const termDateRange = formatTermDateRangeByUserTimeZone(singleTermData?.data?.startTimestamp, singleTermData?.data?.endTimestamp)
  const showTermEditButton = singleTermData.actions?.includes(Actions.EDIT)

  const termControl = (): React.ReactNode => {
    return (
      <>
        <GenericButton
          buttonType={ButtonType.Secondary}
          icon={<Pencil color={grey[250]} />}
          onClick={() => { setShowTermUpdateModal(true) }}
        />
        <GenericModal
          isOpen={showTermUpdateModal}
          maxWidth='sm'
          title={'Edit Term'}
          onClose={() => {
            setShowTermUpdateModal(false)
          }}
          action={false}
        >
          <CreateAndEditTerms selectedRow={singleTermData} handleCloseModal={() => { setShowTermUpdateModal(false) }} />
        </GenericModal>
      </>
    )
  }

  return (
    <Box display='flex' flexDirection='column'>
      {
        (singleTermLoading || !termId) && <LoadingPageV2 title={PAGE_TITLES.TERMS} />
      }
      {
        !singleTermLoading && termId && <>
          <CustomHeader info={singleTermData?.data?.termName} />
          <PageHeader
            breadcrumb={breadcrumbPrefixList}
            title={singleTermData?.data?.termName ?? ''}
            showBackButton
            description={termDateRange && <Typography display={'flex'} gap={2} mt={1} alignItems={'center'} fontWeight={500}><Calendar size={18} color={grey[950]} />{termDateRange}</Typography>}
            controls={showTermEditButton && termControl()}
          />
        </>
      }
      <TermsCourseSectionTab />
      {selectedTab === TabKeys.COURSE_SECTIONS && <TermsSection />}
      {selectedTab === TabKeys.STUDENT_ENROLLMENTS && <Enrollments />}
    </Box>
  )
}
